import { Chip, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttTable, SttTopControls } from '../../sporttia/all';
import { Device } from '../../types/models/DeviceSpace';
import { SttLabelPeriod } from '../../sporttia/all';
import { PERMISSIONS } from '../../lib/permissions';

type DeviceAuth = {
    id?: number;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    devices?: Device[];
};

type ParentItem = {
    id: number;
    ini?: string;
    end?: string;
    dateini?: string;
    dateend?: string;
};

type DeviceListProps = {
    item: ParentItem;
    type: string;
    auths?: DeviceAuth[];
    loaderFunction?: () => void;
    onCreate?: (auth: Partial<DeviceAuth> & { [key: string]: unknown }) => void;
    onSelect?: (row: DeviceAuth) => void;
};

/**
 * DeviceList List page for device authorizations. Device authorizations can be related to groups, activities, events...
 */
export default function DeviceList({
    item,
    type,
    auths,
    loaderFunction = undefined,
    onCreate,
    onSelect,
}: DeviceListProps): React.ReactElement {
    const { t } = useTranslation();

    // Create a new device auth and pass it to the onCreate callback
    function create(): void {
        if (typeof onCreate === 'function') {
            onCreate({
                [type]: item.id,
                dateini: item.ini ?? item.dateini,
                dateend: item.end ?? item.dateend,
                timeini: '08:00:00', // default value
                timeend: '22:00:00',
                weekdays: [0, 1, 2, 3, 4, 5, 6],
            });
        }
    }

    // Select one row in the list
    function select(row: DeviceAuth): void {
        if (typeof onSelect === 'function') {
            onSelect(row);
        }
    }

    return (
        <>
            <SttTopControls
                mb={2}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.DEVICE.CREATE,
                    onClick: create,
                }}
                menu={[]}
            />

            <SttTable
                data={auths ? { rows: auths, count: auths.length } : undefined}
                onFetch={loaderFunction}
                columns={[
                    {
                        title: t('Period'),
                        field: 'period',
                        value: (row: DeviceAuth) => (
                            <Link
                                onClick={() => select(row)}
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                <SttLabelPeriod
                                    ini={row.dateini}
                                    end={row.dateend}
                                />
                            </Link>
                        ),
                    },
                    {
                        title: t('Horary'),
                        field: 'schedule',
                        type: 'hmRange',
                        value: (row: DeviceAuth) => ({
                            ini: `2020-01-01 ${row.timeini}`,
                            end: `2020-01-01 ${row.timeend}`,
                        }),
                    },
                    {
                        title: t('Devices'),
                        field: 'devices',
                        value: (row: DeviceAuth) => (
                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                                {row.devices &&
                                    row.devices.map((device: Device) => (
                                        <Chip
                                            key={device.id}
                                            label={device.name}
                                            size="small"
                                        />
                                    ))}
                            </div>
                        ),
                    },
                    {
                        title: t('WeekDays'),
                        field: 'weekdays',
                        type: 'weekdays',
                        value: (row: DeviceAuth) => row.weekdays,
                    },
                ]}
            />
        </>
    );
}
